import { Handle, Position, type NodeProps } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Edit, EyeOff, Gift, Trash2 } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderBumpNodeData } from '../utils/funnelTypes';

function OrderBumpNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation('funnel');
  const nodeData = data as unknown as OrderBumpNodeData;
  const hasImage = !!nodeData.imageUrl;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
      className={`
        relative rounded-xl border-2 border-dashed bg-[#18181B]/90 backdrop-blur-md shadow-lg
        min-w-[108px] max-w-[200px] overflow-hidden transition-all duration-200 group
        ${selected ? 'border-amber-500 shadow-amber-500/20' : 'border-amber-500/30 hover:border-amber-500/60'}
      `}
    >
      <div className="p-3">
        <div className="flex items-center gap-2.5">
          {hasImage ? (
            <img src={nodeData.imageUrl} alt={nodeData.label}
              className="h-8 w-8 rounded-lg object-cover ring-1 ring-amber-500/30" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 ring-1 ring-amber-500/30">
              <Gift className="h-4 w-4 text-amber-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-[9px] font-medium uppercase tracking-wider text-amber-400/70">{t('order_bump_label')}</p>
              {nodeData.isSecret && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-500/15 border border-violet-500/30 px-1.5 py-0.5 text-[8px] font-medium text-violet-400">
                  <EyeOff className="h-2.5 w-2.5" /> {t('secret')}
                </span>
              )}
            </div>
            <p className="text-xs font-semibold text-white truncate">{nodeData.label}</p>
          </div>
          <div className="flex items-center gap-0.5">
            <button data-action="edit" data-node-type="orderBump"
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-amber-500/20 transition-colors" title={t('edit')}>
              <Edit className="h-3 w-3 text-amber-400 pointer-events-none" />
            </button>
            <button data-action="delete" data-node-type="orderBump"
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-red-500/20 transition-colors" title={t('delete')}>
              <Trash2 className="h-3 w-3 text-red-400 pointer-events-none" />
            </button>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-zinc-400 truncate">{nodeData.offerName}</span>
          <span className="text-xs font-bold text-amber-400 whitespace-nowrap">{nodeData.priceFormatted}</span>
        </div>

        <div className="mt-1.5">
          <span className="inline-flex items-center rounded-full bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 text-[9px] font-medium text-amber-400 truncate">
            {nodeData.callToAction}
          </span>
        </div>
      </div>

      <Handle type="target" position={Position.Left}
        className="!w-2 !h-2 !bg-amber-500 !border-2 !border-[#18181B] !-left-1" />
    </motion.div>
  );
}

export const OrderBumpNode = memo(OrderBumpNodeComponent);
